import { useState } from 'react'
import { Link } from 'react-router'
import { Button, Card, Icon, Meter, Pill, Stack, Text } from '@/design-system'
import { BentoGrid, BentoSpan } from '@/components/Bento'
import { Skeleton } from '@/components/States'
import { PageHeader } from '@/app/layouts/PageHeader'
import { useRepository } from '@/data/repository-context'
import { useQuery } from '@/data/use-query'
import { formatMoney, toMajor } from '@/lib/money'
import { cn } from '@/lib/cn'
import type { Card as CardModel } from '@/data/types'
import { Plastic } from './Plastic'
import { WalletRow } from './WalletRow'
import styles from './CardsScreen.module.css'
import checkout from '@/app/layouts/CheckoutLayout.module.css'
import { BRAND } from '@/brand/brand'

interface Control {
  readonly icon: string
  readonly title: string
  readonly body: string
  readonly to?: string
}

/** Controls differ by kind: a card with no plastic has nothing to replace. */
function controlsFor(card: CardModel): readonly Control[] {
  const shared: readonly Control[] = [
    { icon: 'tune', title: 'Spending limits', body: 'Per transaction and per month' },
    { icon: 'visibility', title: 'Card details', body: 'Number, expiry and CVV' },
  ]

  if (card.kind === 'agent') {
    return [
      { icon: 'storefront', title: 'Allowed merchants', body: 'Where this card may pay' },
      ...shared,
      { icon: 'history', title: 'Agent activity', body: 'Every authorisation it attempted', to: '/transactions' },
      { icon: 'block', title: 'Revoke card', body: 'Stops authorising immediately' },
    ]
  }

  if (card.kind === 'virtual') {
    return [
      { icon: 'add', title: 'Top up card', body: `Free from your ${BRAND.name} balance`, to: '/top-up' },
      ...shared,
      { icon: 'language', title: 'Online payments', body: 'On' },
      { icon: 'sync', title: 'Regenerate number', body: 'Old number stops working at once' },
    ]
  }

  return [
    { icon: 'add', title: 'Top up card', body: `Free from your ${BRAND.name} balance · 0.5% from a debit card`, to: '/top-up' },
    ...shared,
    { icon: 'language', title: 'Online payments', body: 'On' },
    { icon: 'ac_unit', title: 'Freeze card', body: 'Blocks it until you unfreeze' },
    { icon: 'sync', title: 'Replace card', body: 'Lost, stolen or damaged' },
  ]
}

export function CardsScreen() {
  const repository = useRepository()
  const cards = useQuery(() => repository.listCards(), [repository])
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const selected = cards.data?.find((card) => card.id === selectedId) ?? cards.data?.[0]
  const used = selected ? toMajor(selected.spentThisMonth) / toMajor(selected.monthlyLimit) : 0

  return (
    <>
      <PageHeader
        title="Cards"
        subtitle={
          cards.data
            ? `${cards.data.length} cards · spend in any currency at 0.9%`
            : 'Loading your cards'
        }
        actions={
          <>
            <Button variant="ghost" size="md" iconStart="redeem" to="/cashback">
              Cashback
            </Button>
            <Button variant="primary" size="md" iconStart="add" to="/open/card">
              New card
            </Button>
          </>
        }
      />

      <BentoGrid>
        <BentoSpan span={2}>
          <Stack gap={3}>
            {cards.status === 'loading' && <Skeleton height={220} />}

            {selected && (
              <>
                <Link to={`/cards/${selected.id}`} aria-label={`Open ${selected.label}`}>
                  <Plastic card={selected} />
                </Link>

                {cards.data && cards.data.length > 1 && (
                  <div className={styles.rail} role="tablist" aria-label="Your cards">
                    {cards.data.map((card) => (
                      <button
                        key={card.id}
                        type="button"
                        role="tab"
                        aria-selected={card.id === selected.id}
                        className={cn(
                          styles.railItem,
                          card.id === selected.id && styles.railItemOn,
                        )}
                        onClick={() => setSelectedId(card.id)}
                      >
                        <div
                          className={cn(
                            styles.swatch,
                            card.kind === 'virtual' && styles.swatchVirtual,
                            card.kind === 'agent' && styles.swatchAgent,
                          )}
                        />
                        <Text variant="eyebrow" weight="semibold" truncate>
                          {card.label}
                        </Text>
                        <Text variant="eyebrow" tone="secondary" numeric>
                          ••{card.last4}
                        </Text>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </Stack>
        </BentoSpan>

        <BentoSpan span={2}>
          <Stack gap={3}>
            <Card tone="light">
              <Card.Header>
                <Text variant="caption" weight="semibold">
                  This month
                </Text>
                <Pill tone={selected?.status === 'frozen' ? 'warning' : 'positive'}>
                  {selected?.status ?? 'active'}
                </Pill>
              </Card.Header>
              <Text as="p" variant="title" weight="bold" numeric className="pb-mt-3">
                {selected ? formatMoney(selected.spentThisMonth) : '—'}
              </Text>
              <div className="pb-mt-3">
                <Meter value={used} label="Share of the monthly limit used" />
              </div>
              <Text variant="caption" tone="secondary" className="pb-mt-3">
                of {selected ? formatMoney(selected.monthlyLimit) : '—'} monthly limit
              </Text>
            </Card>

            {selected && (
              <Card tone="light">
                <Text variant="caption" weight="semibold">
                  Pay with your phone
                </Text>
                <div className="pb-mt-3">
                  <WalletRow key={selected.id} card={selected} />
                </div>
              </Card>
            )}

            {selected?.agent && (
              <Card tone="dark">
                <Card.Header>
                  <Text variant="caption" weight="semibold" tone="onDark">
                    What this agent may do
                  </Text>
                  <Pill tone="accent">Revocable</Pill>
                </Card.Header>
                <Text variant="caption" tone="onDarkMuted" className="pb-mt-2">
                  {selected.agent.task}
                </Text>
                <Stack gap={0} className="pb-mt-3">
                  {[
                    ['Per transaction', formatMoney(selected.agent.perTransaction)],
                    ['This month', `${formatMoney(selected.spentThisMonth)} of ${formatMoney(selected.monthlyLimit)}`],
                    ['Transactions left', String(selected.agent.transactionsLeft)],
                    ['Stops working', selected.agent.expires],
                  ].map(([label, value]) => (
                    <div key={label} className={checkout.line}>
                      <Text variant="caption" tone="onDarkMuted">
                        {label}
                      </Text>
                      <Text variant="caption" weight="semibold" tone="onDark" numeric>
                        {value}
                      </Text>
                    </div>
                  ))}
                </Stack>
                <Card.Footer>
                  <Text variant="eyebrow" tone="onDarkMuted">
                    Allowed merchants
                  </Text>
                  <div className={`${styles.merchants} pb-mt-2`}>
                    {selected.agent.merchants.map((merchant) => (
                      <Pill key={merchant} tone="onDark">
                        {merchant}
                      </Pill>
                    ))}
                  </div>
                </Card.Footer>
              </Card>
            )}
          </Stack>
        </BentoSpan>

        <BentoSpan span={4}>
          <Card tone="light">
            <Text variant="caption" weight="semibold">
              {selected ? `${selected.label} controls` : 'Controls'}
            </Text>
            <div className={`${styles.controls} pb-mt-2`}>
              {(selected ? controlsFor(selected) : []).map((control) =>
                control.to ? (
                  <Link key={control.title} to={control.to} className={styles.control}>
                    <Icon name={control.icon} size={20} />
                    <Stack grow gap={0} align="start">
                      <Text variant="caption" weight="semibold">
                        {control.title}
                      </Text>
                      <Text variant="caption" tone="secondary">
                        {control.body}
                      </Text>
                    </Stack>
                    <Icon name="chevron_right" size={18} />
                  </Link>
                ) : (
                  <button key={control.title} type="button" className={styles.control}>
                    <Icon name={control.icon} size={20} />
                    <Stack grow gap={0} align="start">
                      <Text variant="caption" weight="semibold">
                        {control.title}
                      </Text>
                      <Text variant="caption" tone="secondary">
                        {control.body}
                      </Text>
                    </Stack>
                    <Icon name="chevron_right" size={18} />
                  </button>
                ),
              )}
            </div>
          </Card>
        </BentoSpan>
      </BentoGrid>
    </>
  )
}
